import type { GameSessionController } from './session-controller';
import { initHero, npcShopCostsFromInit, offlineReportHasActivity, getHeroQuests, getAchievements, getAdventureLog } from '@/network/api';
import type { Achievement, OfflineReport, RoadSegmentPayload, RoadSettlementPayload, HeroResponse } from '@/network/api';
import type { AdventureLogEntry, HeroQuest, Town, TownData } from '@/game/types';
import { getTelegramUserId } from '@/shared/telegram';
import type { Locale, Translations } from '@/i18n';
import { adventureEntriesFromServerLog, type AdventureLogRawRow } from '@/modules/adventure-log/adventureLogMap';
import { heroResponseToState } from './hero-mapper';
import {
  initTownPayloadToTownData, applyInitTownsToEngine, applyInitRoadsToEngine,
  applyInitRoadSettlementsToEngine, hydrateTownsFromApi,
} from './town-mapper';
import type { ChangelogOpen } from './session-store';

interface RefCell<T> { current: T; }
export interface SessionBootstrapPorts {
  setNpcShopCosts: (value: ReturnType<typeof npcShopCostsFromInit>) => void;
  pendingChangelogRef: RefCell<ChangelogOpen | null>;
  setChangelogOpen: (value: ChangelogOpen | null) => void;
  setNeedsName: (value: boolean) => void;
  i18nForLogRef: RefCell<{ locale: Locale; tr: Translations }>;
  baseTownDataRef: RefCell<TownData[]>;
  roadSettlementsRef: RefCell<RoadSettlementPayload[]>;
  setTowns: (value: Town[]) => void;
  townsRef: RefCell<Town[]>;
  setRoadNetwork: (value: Array<Array<{ x: number; y: number }>>, roads?: RoadSegmentPayload[]) => void;
  setOfflineReport: (value: OfflineReport | null) => void;
  setHeroQuests: (value: HeroQuest[]) => void;
  refreshEquipment: () => void;
  prevAchievementsRef: RefCell<Achievement[]>;
  setAchievements: (value: Achievement[]) => void;
  logRawRef: RefCell<AdventureLogRawRow[]>;
  logIdCounter: RefCell<number>;
  setLogEntries: (value: AdventureLogEntry[]) => void;
}

/** REST bootstrap can finish after cancellation; every write is guarded by the owning session. */
export async function bootstrapSession(owner: GameSessionController, ports: SessionBootstrapPorts): Promise<boolean> {
  const { engine, signal, isActive } = owner;
  const {
    setNpcShopCosts, pendingChangelogRef, setChangelogOpen, setNeedsName, i18nForLogRef,
    baseTownDataRef, roadSettlementsRef, setTowns, townsRef, setRoadNetwork, setOfflineReport,
    setHeroQuests, refreshEquipment, prevAchievementsRef, setAchievements, logRawRef, logIdCounter, setLogEntries,
  } = ports;
  let shouldOpenWS = false;
  try {
    const telegramId = getTelegramUserId() ?? 1;
    const initRes = await initHero(telegramId, signal);
    if (!isActive()) return false;
    setNpcShopCosts(npcShopCostsFromInit(initRes));

    if (initRes.showChangelog && initRes.changelog) {
      const bundle: ChangelogOpen = {
        payload: initRes.changelog,
        serverVersion: initRes.serverVersion,
      };
      if (initRes.needsName) {
        pendingChangelogRef.current = bundle;
      } else {
        setChangelogOpen(bundle);
      }
    }

    // Gate game start behind name entry — no hero row until POST /hero/name
    if (initRes.needsName) {
      setNeedsName(true);
      if (initRes.hero) {
        const heroState = heroResponseToState(initRes.hero);
        engine.initFromServer(heroState, initRes.hero.state);
      }
      const loc = i18nForLogRef.current.locale;
      const baseTowns = initRes.towns?.map((t) => initTownPayloadToTownData(t, loc)) ?? [];
      baseTownDataRef.current = baseTowns;
      roadSettlementsRef.current = initRes.roadSettlements ?? [];
      if (initRes.towns?.length) {
        applyInitTownsToEngine(engine, initRes.towns, setTowns, townsRef, loc);
      }
      applyInitRoadsToEngine(engine, initRes.roads, setRoadNetwork);
      applyInitRoadSettlementsToEngine(
        engine,
        initRes.roadSettlements,
        loc,
        baseTowns,
      );
      hydrateTownsFromApi(engine, setTowns, townsRef, setRoadNetwork, loc, isActive)
        .then((layout) => {
          if (isActive() && layout) {
            baseTownDataRef.current = layout.mergedTowns;
            roadSettlementsRef.current = layout.roadSettlements;
          }
        })
        .catch(() => console.warn('[App] Could not fetch towns (name gate)'));
      console.info('[App] Hero needs name, showing name entry screen');
      return false;
    }

    if (!initRes.hero) {
      owner.fail('Invalid server response.');
      return false;
    }

    shouldOpenWS = true;
    const heroState = heroResponseToState(initRes.hero);
    engine.initFromServer(heroState, initRes.hero.state);
    engine.setHeroName(initRes.hero.name);
    console.info('[App] Loaded hero from server, id=', initRes.hero.id);

    if (initRes.offlineReport && offlineReportHasActivity(initRes.offlineReport)) {
      const r = initRes.offlineReport;
      console.info(
        `[Offline] ${r.message ?? ''} Killed ${r.monstersKilled} monsters, +${r.xpGained} XP, +${r.goldGained} gold, +${r.levelsGained} levels, deaths ${r.deaths ?? 0}, revives ${r.revives ?? 0}`,
      );
      setOfflineReport(r);
    }

    const loc = i18nForLogRef.current.locale;
    const baseTowns = initRes.towns?.map((t) => initTownPayloadToTownData(t, loc)) ?? [];
    baseTownDataRef.current = baseTowns;
    roadSettlementsRef.current = initRes.roadSettlements ?? [];
    if (initRes.towns?.length) {
      applyInitTownsToEngine(engine, initRes.towns, setTowns, townsRef, loc);
    }
    applyInitRoadsToEngine(engine, initRes.roads, setRoadNetwork);
    applyInitRoadSettlementsToEngine(
      engine,
      initRes.roadSettlements,
      loc,
      baseTowns,
    );
    hydrateTownsFromApi(engine, setTowns, townsRef, setRoadNetwork, loc, isActive)
      .then((layout) => {
        if (isActive() && layout) {
          baseTownDataRef.current = layout.mergedTowns;
          roadSettlementsRef.current = layout.roadSettlements;
        }
      })
      .catch(() => console.warn('[App] Could not fetch towns'));

    getHeroQuests(telegramId)
      .then((q) => { if (isActive()) setHeroQuests(q); })
      .catch(() => console.warn('[App] Could not fetch hero quests'));
    refreshEquipment();

    getAchievements(telegramId)
      .then((a) => { if (isActive()) { prevAchievementsRef.current = a; setAchievements(a); } })
      .catch(() => console.warn('[App] Could not fetch achievements'));

    // Fetch adventure log (same source as server DB; response shape { log: [...] })
    try {
      const serverLog = await getAdventureLog(telegramId, 50);
      if (!isActive()) return false;
      const { locale: loc, tr: bundle } = i18nForLogRef.current;
      const { entries, rawRows, maxId } = adventureEntriesFromServerLog(serverLog, loc, bundle);
      logRawRef.current = rawRows;
      logIdCounter.current = Math.max(logIdCounter.current, maxId);
      setLogEntries(entries);
    } catch {
      console.warn('[App] Could not fetch adventure log');
    }
  } catch (err) {
    if (!isActive()) return false;
    owner.fail('Cannot reach game server. Please try again later.');
    console.error('[App] Backend not available. Game requires a server connection:', err);
    return false;
  }
  return isActive() && shouldOpenWS;
}

/** Finish onboarding through the same owner, including guarded late layout/UI data responses. */
export function completeNamedSession(owner: GameSessionController, hero: HeroResponse, ports: SessionBootstrapPorts): void {
  if (!owner.isActive()) return;
  const { engine, isActive } = owner;
  const {
    setNeedsName, i18nForLogRef, setTowns, townsRef, setRoadNetwork, setHeroQuests,
    logRawRef, logIdCounter, setLogEntries, prevAchievementsRef, setAchievements, pendingChangelogRef, setChangelogOpen,
    baseTownDataRef, roadSettlementsRef,
  } = ports;
  setNeedsName(false);
  owner.applyNamedHero(hero);
  const telegramId = getTelegramUserId() ?? 1;
  const loc = i18nForLogRef.current.locale;
  hydrateTownsFromApi(engine, setTowns, townsRef, setRoadNetwork, loc, isActive).then((layout) => {
    if (isActive() && layout) {
      baseTownDataRef.current = layout.mergedTowns;
      roadSettlementsRef.current = layout.roadSettlements;
    }
  }).catch(() =>
    console.warn('[App] Could not fetch towns'),
  );
  getHeroQuests(telegramId)
    .then((q) => { if (isActive()) setHeroQuests(q); })
    .catch(() => console.warn('[App] Could not fetch hero quests'));
  getAdventureLog(telegramId, 50)
    .then((serverLog) => {
      if (!isActive()) return;
      const { locale: loc, tr: bundle } = i18nForLogRef.current;
      const { entries, rawRows, maxId } = adventureEntriesFromServerLog(serverLog, loc, bundle);
      logRawRef.current = rawRows;
      logIdCounter.current = Math.max(logIdCounter.current, maxId);
      setLogEntries(entries);
    })
    .catch(() => console.warn('[App] Could not fetch adventure log'));
  getAchievements(telegramId)
    .then((a) => { if (isActive()) { prevAchievementsRef.current = a; setAchievements(a); } })
    .catch(() => {});

  if (pendingChangelogRef.current) {
    setChangelogOpen(pendingChangelogRef.current);
    pendingChangelogRef.current = null;
  }
}
